export interface JsonResponse<T> {
  data: T | null;
  status: number | null;
  error?: { message: string };
}

abstract class FortnoxClient {
  public apiEndpoint: string;

  constructor(apiEndpoint: string) {
    this.apiEndpoint = apiEndpoint;
  }

  protected getApiUrl(
    action: string,
    id?: string,
    queryParams?:
      | string[][]
      | Record<string, string | string>
      | string
      | URLSearchParams,
  ): string {
    let url = `${this.apiEndpoint}/${action}`;

    if (id != null) {
      url += `/${id}`;
    }

    if (queryParams != null) {
      url += `?${new URLSearchParams(queryParams).toString()}`;
    }

    return url;
  }

  /**
   * Send a request to the specified url. Tries to parse the response as json and returns a wrapper response with the json data, http status response, and any potential errors.
   */
  protected async fetchJson<T>(
    url: string,
    init?: RequestInit,
    headers?: Record<string, string>,
  ): Promise<JsonResponse<T>> {
    try {
      const response = await this.fetch(url, init, headers);

      if (!response.ok) {
        console.error("Non-successful api response", response);

        let error: { message: string } | undefined = undefined;

        try {
          let json = await response.json();

          if ("Error" in json) {
            // asp.net exception response
            const message = json.Error.Message;
            error = { message };
          } else if ("error" in json && typeof json.error == "string") {
            // custom response type error
            error = { message: json.error };
          } else {
            // unknown error
            error = { message: "Förfrågan misslyckades" };
            console.error("Unknown error response data", json);
          }
        } catch {
          error = { message: "Förfrågan misslyckades" };
        }

        return { data: null, status: response.status, error };
      }

      try {
        return { data: await response.json(), status: response.status };
      } catch (error: any) {
        // user session has most likely expired which causes the response to redirect to the login page which is not json
        return {
          data: null,
          status: 401,
          error: {
            message: "Du har blivit utloggad, logga in igen",
          },
        };
      }
    } catch (error: any) {
      console.error("Unknown error raised during api request", error);

      return {
        data: null,
        status: null,
        error: {
          message: error.message?.toString() ?? "Förfrågan misslyckades",
        },
      };
    }
  }

  /**
   * Send a request to the specified url. Returns the 'raw' response. The caller is responsible for handling errors.
   *
   * Consider using `fetchJson` instead which handles all types of errors and returns a typed response.
   */
  protected fetch(
    url: string,
    init?: RequestInit,
    headers?: Record<string, string>,
  ): Promise<Response> {
    return fetch(url, {
      headers: {
        "Content-Type": "application/json",
        ...(headers ?? {}),
      },
      ...init,
    });
  }
}

export default FortnoxClient;
